import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ORIGIN, getAllLabReports } from '../../api';
import styles from './LabReportHistory.module.css';

const LabReportHistory = () => {
  const [labReports, setLabReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const getFileUrl = (filePath) => {
    if (!filePath) return '';
    const asString = String(filePath);
    if (/^https?:\/\//i.test(asString)) return asString;
    // Normalize Windows paths coming from MongoDB (multer may store backslashes).
    const normalized = asString.replace(/\\/g, '/').replace(/^\/+/, '');
    return `${API_ORIGIN}/${normalized}`;
  };

  useEffect(() => {
    const fetchLabReports = async () => {
      try {
        setLoading(true);
        const data = await getAllLabReports(); // Fetch all reports for global visibility
        // Ensure we have an array and filter out any invalid entries
        const validReports = Array.isArray(data) ? data.filter(report => report && typeof report === 'object') : [];
        setLabReports(validReports);
        setError(null);
      } catch (err) {
        setError('Failed to load lab reports');
        console.error('Error fetching lab reports:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLabReports();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleBack = () => {
    navigate('/user/dashboard');
  };

  const handleDownload = (filePath, fileName) => {
    // Create a link to download the file
    const link = document.createElement('a');
    link.href = getFileUrl(filePath);
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.loadingContainer}>
            <h2 className={styles.loadingText}>Loading lab reports...</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>Lab Report History</h1>
          <button
            onClick={handleBack}
            className={styles.backButton}
          >
            Back to Dashboard
          </button>
        </div>

        <div className={styles.mainContent}>
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}

          {labReports.length === 0 ? (
            <div className={styles.emptyState}>
              <h3>No lab reports found</h3>
              <p>You don't have any lab reports yet.</p>
            </div>
          ) : (
            <div className={styles.reportsGrid}>
              {labReports.map((report) => (
                <div key={report._id} className={styles.reportCard}>
                  <div className={styles.reportHeader}>
                    <h3 className={styles.reportTitle}>{report.fileName}</h3>
                    <p className={styles.reportMeta}>
                      <strong>Uploaded:</strong> {formatDate(report.uploadedAt)}
                    </p>
                    {report.description && (
                      <p className={styles.reportDescription}>
                        <strong>Description:</strong> {report.description}
                      </p>
                    )}
                    <p className={styles.reportPatient}>
                      <strong>Patient ID:</strong> {typeof report.patient === 'string' ? report.patient : (report.patient?.patientName || report.patient?._id || 'Unknown Patient')}
                    </p>
                  </div>

                  <div className={styles.buttonGroup}>
                    <button
                      onClick={() => handleDownload(report.filePath, report.fileName)}
                      className={styles.downloadButton}
                    >
                      Download Report
                    </button>
                    <button
                      onClick={() => window.open(getFileUrl(report.filePath), '_blank')}
                      className={styles.viewButton}
                    >
                      View Report
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LabReportHistory; 
